import { Loader2, LockKeyhole } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";

import type { DialogComponentProps } from "@/shared/components/dialog";
import { ROUTE_PATHS } from "@/shared/constants/route-paths";
import type { UnifiedError } from "@/shared/types/api";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";
import { Label } from "@/shared/ui/label";

import { ManualPasswordInput } from "./manual-password-input";
import { useUpdateFolderMutation } from "../../hooks/use-mine-mutations";
import type { Folder } from "../../navigation/types";
import { savePrivacyPassword, unlockPrivacy } from "../../privacy/api";
import { activatePrivacySession, lockPrivacySession } from "../../privacy/session";
import { usePrivacySession } from "../../privacy/use-privacy-session";

type PrivacyMode = "protect" | "unprotect" | "unlock" | "manage";
type Stage = "account" | "configure" | "unlock" | "confirm";

interface PrivacyFolderDialogProps extends DialogComponentProps<boolean> {
  mode: PrivacyMode;
  folder?: Folder;
  hasAccountPassword: boolean;
  privacyConfigured: boolean;
  onSetAccountPassword: (password: string) => Promise<void>;
  onPrivacyChanged: () => void;
}

export function PrivacyFolderDialog({
  close,
  resolve,
  mode,
  folder,
  hasAccountPassword,
  privacyConfigured,
  onSetAccountPassword,
  onPrivacyChanged,
}: PrivacyFolderDialogProps) {
  const { isUnlocked } = usePrivacySession();
  const [accountReady, setAccountReady] = useState(hasAccountPassword);
  const [configured, setConfigured] = useState(privacyConfigured);
  const [stage, setStage] = useState<Stage>(() => {
    if (!hasAccountPassword) return "account";
    if (!privacyConfigured || mode === "manage") return "configure";
    if (!isUnlocked) return "unlock";
    return mode === "unlock" ? "unlock" : "confirm";
  });
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [masterPassword, setMasterPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const accountPasswordId = useId();
  const passwordId = useId();
  const confirmationId = useId();
  const updateFolder = useUpdateFolderMutation();

  const title = stage === "account" ? "先设置账户密码"
    : stage === "configure" ? (configured ? "重置隐私密码" : "设置隐私密码")
    : stage === "unlock" ? (mode === "unlock" ? "解锁私密文件夹" : "验证隐私密码")
    : mode === "unprotect" ? "取消私密保护" : "设为私密文件夹";

  async function submit() {
    setError("");
    if (stage !== "confirm" && !password) {
      setError("请输入密码。");
      return;
    }
    if ((stage === "account" || stage === "configure") && password !== confirmation) {
      setError("两次输入的密码不一致。");
      return;
    }
    if (stage === "configure") {
      const byteLength = new TextEncoder().encode(password).length;
      if (byteLength < 8) {
        setError("隐私密码至少需要 8 位英文、数字或符号。");
        return;
      }
      if (byteLength > 64) {
        setError("隐私密码太长，请缩短后重试。");
        return;
      }
      if (!masterPassword) {
        setError("请输入账户密码以验证身份。");
        return;
      }
    }
    setBusy(true);
    try {
      if (stage === "account") {
        await onSetAccountPassword(password);
        setAccountReady(true);
        setStage("configure");
        toast.success("账户密码已设置");
      } else if (stage === "configure") {
        await savePrivacyPassword(masterPassword, password);
        lockPrivacySession({ closeDialogs: false });
        onPrivacyChanged();
        setConfigured(true);
        toast.success(configured ? "隐私密码已重置" : "隐私密码已设置");
        if (mode === "manage") resolve(true);
        else setStage("unlock");
      } else if (stage === "unlock") {
        const reply = await unlockPrivacy(password);
        activatePrivacySession(reply.token, reply.expires_at);
        toast.success(mode === "unlock" ? "已解锁，有效期 15 分钟" : "隐私密码验证成功");
        if (mode === "unlock") resolve(true);
        else setStage("confirm");
      } else if (folder) {
        await updateFolder.mutateAsync({
          id: folder.id,
          name: folder.name,
          privacy_enabled: mode === "protect",
        });
        toast.success(mode === "protect" ? "已设为私密文件夹" : "已取消私密保护");
        resolve(true);
      }
      setPassword("");
      setConfirmation("");
      setMasterPassword("");
    } catch (cause) {
      if ((cause as UnifiedError)?.reason === "PRIVACY_LOCKED") setStage("unlock");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-border/70 bg-muted/40 p-3">
        <LockKeyhole className="mt-0.5 size-4 shrink-0" />
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            {stage === "account" ? "该账户尚未设置登录密码。请先设置，用于以后重置隐私密码。"
              : stage === "configure" ? "使用账户密码验证身份。忘记隐私密码时，也可在这里重置；资料不会丢失。"
              : stage === "unlock" ? mode === "unlock"
                ? "一次解锁可查看本标签页的全部私密文件夹，15 分钟后自动锁定。"
                : `输入隐私密码以继续${mode === "protect" ? "设置" : "取消"}私密保护。验证后，本标签页的私密内容也可在 15 分钟内查看。`
              : mode === "protect" ? `“${folder?.name}”及其子文件夹和内容将受私密保护。此功能限制访问，不会加密文件内容。`
              : `取消“${folder?.name}”的保护；单独设为私密的子文件夹仍会保持私密。`}
          </p>
        </div>
      </div>
      {stage !== "confirm" && <div className="space-y-4">
        {stage === "configure" && <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor={accountPasswordId} className="text-xs text-muted-foreground">账户密码</Label>
            <a className="text-xs text-muted-foreground underline hover:text-foreground" href={ROUTE_PATHS.SEND_RESET_LINK} target="_blank" rel="noreferrer">
              忘记账户密码？
            </a>
          </div>
          <ManualPasswordInput id={accountPasswordId} value={masterPassword} onChange={(event) => setMasterPassword(event.target.value)} />
        </div>}
        <div className="space-y-2">
          <Label htmlFor={passwordId} className="text-xs text-muted-foreground">{stage === "account" ? "新账户密码" : stage === "configure" ? "新隐私密码" : "隐私密码"}</Label>
          <ManualPasswordInput id={passwordId} value={password} onChange={(event) => setPassword(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void submit(); }} />
        </div>
        {(stage === "account" || stage === "configure") && <div className="space-y-2">
          <Label htmlFor={confirmationId} className="text-xs text-muted-foreground">确认密码</Label>
          <ManualPasswordInput id={confirmationId} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void submit(); }} />
        </div>}
        {stage === "unlock" && accountReady && <button type="button" className="text-xs text-muted-foreground underline hover:text-foreground" onClick={() => { setPassword(""); setStage("configure"); setError(""); }}>
          忘记隐私密码？使用账户密码重置
        </button>}
      </div>}
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={close} disabled={busy}>取消</Button>
        <Button type="button" onClick={() => void submit()} disabled={busy}>
          {busy && <Loader2 className="size-4 animate-spin" />}
          {stage === "confirm" ? "确认" : stage === "unlock" ? mode === "unlock" ? "解锁" : "验证并继续" : "继续"}
        </Button>
      </DialogFooter>
    </div>
  );
}
